import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import return_services from "services/refund_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import {
  RefundRequest,
  SettleRefundPayload,
  RejectRefundPayload,
  REFUND_STATUS_CODE,
} from "types/refund";

export const RETURN_REJECT_REASON = "merchant rejected the return request";

export function useRefundData(merchantId: string) {
  const queryClient = useQueryClient();
  const networkType = useSelector(networkTypeSelector);
  const { param: token } = useSettlementToken();

  // Filtered to the selected token server-side, so the token is part of the key. This also makes
  // `pendingCount` below per-token: the redeem guard now blocks on unsettled refunds IN THE TOKEN
  // being redeemed, not across all of them. That is the tighter reading of the rule — a pending
  // USDC refund is a claim on USDC, and cannot be paid out of a VIP balance.
  const returnsQuery = useQuery({
    queryKey: ["refunds", merchantId, token],
    queryFn: () => return_services.getRefunds(merchantId, networkType, token),
    enabled: !!merchantId,
  });

  const settleMutation = useMutation({
    mutationFn: (payload: SettleRefundPayload) =>
      return_services.settleReturn(payload, networkType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["refunds", merchantId] });
      // Settling MOVES MONEY: approveRefund ends in safeTransferFrom(merchant -> cardAlias), so
      // the balance drops and a new outgoing transfer appears. Both are keyed by merchantADDRESS,
      // which this hook does not have — hence the bare prefixes, as useRefreshMerchant uses.
      // Without these the headline balance still shows the pre-refund figure, and it is the same
      // figure the redeem guard checks. (rejectMutation moves nothing, so it needs neither.)
      queryClient.invalidateQueries({ queryKey: ["merchantBalance"] });
      queryClient.invalidateQueries({ queryKey: ["merchantTransactions"] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (payload: RejectRefundPayload) =>
      return_services.rejectReturn(payload, networkType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["refunds", merchantId] });
    },
  });

  const refunds = returnsQuery.data ?? [];
  const pendingCount = refunds.filter(
    (r: RefundRequest) => r.status === REFUND_STATUS_CODE.PENDING,
  ).length;

  return {
    refunds,
    refundsLoading: returnsQuery.isLoading,
    refreshRefunds: () =>
      queryClient.invalidateQueries({ queryKey: ["refunds", merchantId] }),
    pendingCount,
    settleRefund: settleMutation.mutate,
    settleLoading: settleMutation.isPending,
    rejectRefund: rejectMutation.mutate,
    rejectLoading: rejectMutation.isPending,
  };
}
